import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import { INVITE_VALUE_STATE_NONE, type InviteValueState } from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import type Dialog from "sap/m/Dialog";
import type Input from "sap/m/Input";
import type Select from "sap/m/Select";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { Select$ChangeEvent } from "sap/m/Select";
import type { Link$PressEvent } from "sap/m/Link";
import type ResourceBundle from "sap/base/i18n/ResourceBundle";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";
import { showInviteOutcome, type InviteUserResponse } from "../util/InviteOutcome";

type UserTypeKey =
	| "ALL"
	| "ComputecAdmin" | "ComputecAuditor" | "ComputecUser" | "ComputecMarketing" | "ComputecDeveloper"
	| "PartnerAdmin" | "PartnerUser"
	| "CustomerAdmin" | "CustomerUser" | "CustomerLicenseOnly";

type StatusKey = "ALL" | "Pending" | "Active" | "Disabled" | "Removed";

type AccessKey = "all" | "portal" | "licenseOnly";

interface VariantOption {
	key: string;
	text: string;
}

interface OrgOption {
	Id: string;
	Name: string;
}

interface ViewState {
	searchQuery: string;
	partnerOrgIdFilter: string;     // "ALL" | <guid>
	customerOrgIdFilter: string;    // "ALL" | <guid>
	userTypeFilter: UserTypeKey;
	statusFilter: StatusKey;
	accessFilter: AccessKey;
	variantKey: string;
	availableVariants: VariantOption[];

	// Invite dialog state (fragment InviteUserDialog uses these).
	inviteShowOrgPicker: boolean;
	inviteOrgId: string;
	inviteCompuTecOrgId: string;
	inviteHasPortalAccess: boolean;
	inviteValueState: InviteValueState;
}

interface VariantPreset {
	userType: UserTypeKey;
	status: StatusKey;
	search: string;
}

// Predefiniowane warianty — gotowe zestawy filtrów. Wzorzec analogiczny do Organizations.
const VARIANTS: Record<string, VariantPreset> = {
	"standard":      { userType: "ALL", status: "ALL", search: "" },
	"active-portal": { userType: "ALL", status: "Active", search: "" },
	"license-only":  { userType: "CustomerLicenseOnly", status: "ALL", search: "" },
	"pending":       { userType: "ALL", status: "Pending", search: "" },
	"disabled":      { userType: "ALL", status: "Disabled", search: "" }
};

const VARIANT_KEYS = ["standard", "active-portal", "license-only", "pending", "disabled"];

// Karty statystyk nad tabelą — ten sam fragment StatCardsRow co Organizations/Dashboard.
const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "users.stats.total.label", subtitleKey: "users.stats.total.subtitle", state: "Neutral" },
	{ key: "active", labelKey: "users.stats.active.label", subtitleKey: "users.stats.active.subtitle", state: "Good", filter: "Status eq 'Active'" },
	{ key: "pending", labelKey: "users.stats.pending.label", subtitleKey: "users.stats.pending.subtitle", state: "Critical", filter: "Status eq 'Pending'" },
	{ key: "disabled", labelKey: "users.stats.disabled.label", subtitleKey: "users.stats.disabled.subtitle", state: "Error", filter: "Status eq 'Disabled'" }
];


interface InviteOrgRow {
	Id: string;
	Name: string;
	Path: string;
	Type: string;
}

/**
 * Second text of an invite-picker item: the organization type and, for a partner's customer, the
 * partner's name. The parent is the second-to-last segment of `Path` (`id_<guid N>` per level),
 * looked up among the organizations the caller can see; a parent outside that set adds nothing.
 */
function describeInviteOrg(org: InviteOrgRow, byPathSegment: Map<string, InviteOrgRow>, bundle: ResourceBundle): string {
	const type = bundle.getText(`organization.type.${org.Type}`) ?? org.Type;
	if (org.Type !== "EndCustomer") {
		return type;
	}
	const segments = org.Path.toLowerCase().split(".");
	const parent = segments.length > 1 ? byPathSegment.get(segments[segments.length - 2] ?? "") : undefined;
	return parent?.Type === "Partner" ? `${type} · ${parent.Name}` : type;
}

/**
 * @namespace computec.b2b.portal.controller
 */
export default class PortalUsers extends ListPageController {
	public override onInit(): void {
		const initial: ViewState = {
			searchQuery: "",
			partnerOrgIdFilter: "ALL",
			customerOrgIdFilter: "ALL",
			userTypeFilter: "ALL",
			statusFilter: "ALL",
			accessFilter: "all",
			variantKey: "standard",
				availableVariants: [],
			inviteShowOrgPicker: true,
			inviteOrgId: "",
			inviteCompuTecOrgId: "",
			inviteHasPortalAccess: true,
			inviteValueState: { ...INVITE_VALUE_STATE_NONE }
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getView().setModel(new JSONModel({ items: [] }), "partnerOptions");
		this.getView().setModel(new JSONModel({ items: [] }), "customerOptions");
		this.getView().setModel(new JSONModel({ items: [] }), "inviteOrgOptions");

		void this.initStatsCards("Users", STATS);
		this.getRouter().getRoute("portalUsers")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		this.setPageHeader({
			title: bundle.getText("users.title") ?? "Portal users",
			subtitle: bundle.getText("users.subtitle") ?? ""
		});

		const model = this.getView().getModel("view") as JSONModel;
		const variantOptions: VariantOption[] = VARIANT_KEYS.map(key => ({
			key,
			text: bundle.getText(`users.variant.${variantKeyToI18n(key)}`) ?? key
		}));
		model.setProperty("/availableVariants", variantOptions);

		await Promise.all([
			this.loadOrgOptions("Partner", "partnerOptions", bundle.getText("users.filter.partner.all") ?? "All partners"),
			this.loadOrgOptions("EndCustomer", "customerOptions", bundle.getText("users.filter.customer.all") ?? "All customers")
		]);

		this.applyFilters();
		void this.refreshStatsCards();
	}

	/**
	 * Ładuje listę organizacji wybranego typu do JSON modelu używanego jako items binding
	 * w Select-ach Partner / Customer. Dodaje na początek pozycję ALL.
	 *
	 * Świadomie używamy raw OData URL przez authFetch zamiast bindingu Select.items z parameters
	 * — gotcha z sapui5-b2b: filter na custom .NET enum types przez UI5 Filter object failuje,
	 * a parameters w XML attrybucie wymaga eskejpowania apostrofów. Prostsze i bezpieczniejsze.
	 */
	private async loadOrgOptions(orgType: string, modelName: string, allLabel: string): Promise<void> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const url = `/odata/v4/Organizations?$filter=Type eq '${orgType}'&$orderby=Name&$select=Id,Name`;
			const resp = await fetcher.fetch(url, { headers: { Accept: "application/json" } });
			if (!resp.ok) {
				return;
			}
			const body = await resp.json() as { value?: OrgOption[] };
			const items: OrgOption[] = [{ Id: "ALL", Name: allLabel }, ...(body.value ?? [])];
			(this.getView().getModel(modelName) as JSONModel).setProperty("/items", items);
		} catch {
			// Best-effort — Select pokaże tylko ALL, lista userów nie jest zablokowana.
		}
	}

	// === Filters / search / variants ===

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/searchQuery", raw);
		this.applyFilters();
	}

	public onPartnerSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "ALL";
		(this.getView().getModel("view") as JSONModel).setProperty("/partnerOrgIdFilter", key);
		this.applyFilters();
	}

	public onCustomerSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "ALL";
		(this.getView().getModel("view") as JSONModel).setProperty("/customerOrgIdFilter", key);
		this.applyFilters();
	}

	public onUserTypeSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as UserTypeKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/userTypeFilter", key);
		this.applyFilters();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onAccessFilterChange(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "all") as AccessKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/accessFilter", key);
		this.applyFilters();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "standard";
		const preset = VARIANTS[key];
		if (!preset) {
			return;
		}
		const model = this.getView().getModel("view") as JSONModel;
		model.setProperty("/variantKey", key);
		model.setProperty("/userTypeFilter", preset.userType);
		model.setProperty("/statusFilter", preset.status);
		model.setProperty("/searchQuery", preset.search);
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("usersTable");
		void this.refreshStatsCards();
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		// Raw $filter omija UI5 _Helper.formatLiteral który nie umie custom .NET enums
		// (PortalUserStatus, OrganizationType). GUID literal: bez apostrofów (Edm.Guid).
		if (state.searchQuery.trim().length > 0) {
			const q = escapeODataString(state.searchQuery.trim().toLowerCase());
			parts.push(`(contains(tolower(Email),'${q}') or contains(tolower(FullName),'${q}'))`);
		}
		if (state.partnerOrgIdFilter !== "ALL") {
			parts.push(`PartnerOrgId eq ${state.partnerOrgIdFilter}`);
		}
		if (state.customerOrgIdFilter !== "ALL") {
			parts.push(`CustomerOrgId eq ${state.customerOrgIdFilter}`);
		}
		if (state.userTypeFilter !== "ALL") {
			parts.push(`UserType eq '${state.userTypeFilter}'`);
		}
		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}
		if (state.accessFilter === "portal") {
			parts.push("HasPortalAccess eq true");
		} else if (state.accessFilter === "licenseOnly") {
			// Technical accounts have no portal access either, but hold no licenses (spec 29 § D-133).
			parts.push("HasPortalAccess eq false and UserType ne 'System'");
		}

		const binding = this.getTableBinding("usersTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	// === Navigation ===

	public onOpenUser(event: Link$PressEvent): void {
		const id = event.getSource().getBindingContext("odata")?.getProperty("Id") as string | undefined;
		if (id) {
			this.navTo("portalUserDetail", { id });
		}
	}

	public onOpenOrganization(event: Link$PressEvent): void {
		const id = event.getSource().getBindingContext("odata")?.getProperty("OrganizationId") as string | undefined;
		if (id) {
			this.navToOrganization(id);
		}
	}

	public onOpenPartner(event: Link$PressEvent): void {
		const id = event.getSource().getBindingContext("odata")?.getProperty("PartnerOrgId") as string | undefined;
		if (id) {
			this.navToOrganization(id);
		}
	}

	public onOpenCustomer(event: Link$PressEvent): void {
		const id = event.getSource().getBindingContext("odata")?.getProperty("CustomerOrgId") as string | undefined;
		if (id) {
			this.navToOrganization(id);
		}
	}

	// === Invite user dialog ===

	private inviteUserDialog?: Dialog;

	public async onAddUserPress(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;

		if (!this.inviteUserDialog) {
			this.inviteUserDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.InviteUserDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.inviteUserDialog);
		}

		// Reset pól na każde otwarcie — Fragment.load zwraca cached instance.
		(this.byId("inpInviteEmail") as Input).setValue("");
		(this.byId("inpInviteFirstName") as Input).setValue("");
		(this.byId("inpInviteLastName") as Input).setValue("");
		(this.byId("ddInviteRole") as Select).setSelectedKey("User");
		view.setProperty("/inviteOrgId", "");
		view.setProperty("/inviteHasPortalAccess", true);
		view.setProperty("/inviteValueState", { ...INVITE_VALUE_STATE_NONE });

		await this.loadInviteOrgOptions();
		this.inviteUserDialog.open();
	}

	/**
	 * Pobiera wszystkie organizacje widoczne dla wołającego — backend `TenantScope.VisibleOrganizationsForUser`
	 * już filtruje multi-tenant scope (CompuTec admin → wszystko; Partner → swoja org + dzieci itd.),
	 * więc nie powielamy filtra w SPA. CompuTec admin musi widzieć CompuTec, żeby invitować computec-userów.
	 * Sortowanie po Path — partnerzy najpierw, pod nimi ich klienci.
	 */
	private async loadInviteOrgOptions(): Promise<void> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const url = "/odata/v4/Organizations?$orderby=Path&$select=Id,Name,Path,Type";
			const resp = await fetcher.fetch(url, { headers: { Accept: "application/json" } });
			if (!resp.ok) {
				return;
			}
			const body = await resp.json() as { value?: InviteOrgRow[] };
			const rows = body.value ?? [];
			const bundle = await this.getResourceBundle();
			const byPathSegment = new Map(rows.map(o => [`id_${o.Id.replace(/-/g, "").toLowerCase()}`, o]));
			const items = rows.map(o => ({ Id: o.Id, Name: o.Name, Details: describeInviteOrg(o, byPathSegment, bundle) }));
			(this.getView().getModel("inviteOrgOptions") as JSONModel).setProperty("/items", items);
			// Card 31: the staff hint shows when the picked organization is CompuTec.
			const compuTec = rows.find(o => o.Type === "CompuTec");
			(this.getView().getModel("view") as JSONModel).setProperty("/inviteCompuTecOrgId", compuTec?.Id ?? "");
		} catch {
			// Best-effort — Select pozostanie pusty, user dostanie error przy submit.
		}
	}

	public onCancelInviteUser(): void {
		this.inviteUserDialog?.close();
	}

	public onInvitePortalAccessChange(): void {
		// The state is already updated by the two-way binding on Switch.state; the fragment
		// carries visible="{view>/inviteHasPortalAccess}" on the Role field.
	}

	public async onSaveInviteUser(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;

		const orgId = (view.getProperty("/inviteOrgId") as string).trim();
		const email = (this.byId("inpInviteEmail") as Input).getValue().trim();
		const firstName = (this.byId("inpInviteFirstName") as Input).getValue().trim();
		const lastName = (this.byId("inpInviteLastName") as Input).getValue().trim();
		const hasPortalAccess = view.getProperty("/inviteHasPortalAccess") as boolean;
		const role = (this.byId("ddInviteRole") as Select).getSelectedKey() as "User" | "Admin" | "Auditor" | "MarketingManager" | "Developer";

		// Each empty field is flagged on its own and the message names everything that is missing;
		// the organization alone keeps its dedicated "Select an organization." message.
		const fields: { key: keyof InviteValueState; value: string; label: string }[] = [
			{ key: "org", value: orgId, label: "inviteUser.field.organization" },
			{ key: "email", value: email, label: "organization.detail.users.dialog.email" },
			{ key: "firstName", value: firstName, label: "organization.detail.users.dialog.firstName" },
			{ key: "lastName", value: lastName, label: "organization.detail.users.dialog.lastName" }
		];
		const missing = fields.filter(f => f.value.length === 0);
		view.setProperty("/inviteValueState", {
			...INVITE_VALUE_STATE_NONE,
			...Object.fromEntries(missing.map(f => [f.key, "Error"]))
		});
		if (missing.length === 1 && missing[0].key === "org") {
			MessageBox.warning(bundle.getText("inviteUser.error.organizationRequired") ?? "Select an organization.");
			return;
		}
		if (missing.length > 0) {
			const names = missing.map(f => bundle.getText(f.label) ?? f.key).join(", ");
			MessageBox.warning(bundle.getText("inviteUser.error.missingFields", [names]) ?? `Fill in the required fields: ${names}.`);
			return;
		}

		// ADR-014: HasPortalAccess=false → Role MUST be null.
		const payload = {
			email,
			firstName,
			lastName,
			hasPortalAccess,
			role: hasPortalAccess ? role : null
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const response = await fetcher.json<InviteUserResponse>(
				`/api/v1/organizations/${orgId}/users/invite`,
				{ method: "POST", body: JSON.stringify(payload) }
			);
			this.inviteUserDialog?.close();
			showInviteOutcome(bundle, response);

			this.refreshTable("usersTable");
		} catch (e) {
			const err = e as ResponseError;
			const fallback = bundle.getText("organization.detail.users.dialog.failure") ?? "Nie udało się zaprosić użytkownika.";
			// Grant ceiling: the backend refuses a role that carries permissions the caller does
			// not hold. The generic message would leave the admin guessing at the invitee's data;
			// the cause is the caller's own set. There is no self-target case here - the invitee
			// does not exist yet.
			if ((err.bodyText ?? "").includes("grant.exceeds_caller_permissions")) {
				MessageBox.error(
					bundle.getText("organization.detail.users.dialog.errorExceedsCaller")
					?? "You cannot invite a user with a role that grants more than you hold yourself.");
				return;
			}
			MessageBox.error(problemMessage(err.bodyText, fallback));
		}
	}
}

/** Escape a single quote per OData v4 string literal rules: ' → '' */
function escapeODataString(value: string): string {
	return value.replace(/'/g, "''");
}

/** "active-portal" → "activePortal" — pasuje do i18n key konwencji. */
function variantKeyToI18n(key: string): string {
	return key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}
